// ***************************************
// Definition of game token InventoryController
// ***************************************
import { MiscSettings, PowerUpSettings } from "../settings/Settings";
import { InventoryPowerUpSlotModel } from "./InventoryPowerUpSlotModel";
import { YoutubeStorageManager } from "./storage/YoutubeStorageManager";
/**
 * The constructor for the InventoryController game token.
 * @constructor
 */
export var InventoryController = function (powerUpSettings) {
    "use strict";
    this._powerUpSettings = powerUpSettings;
};
InventoryController.prototype._powerUpSettings = {};
/**
 * The end timestamp of the cooldown
 * @type {Number}
 * @private
 */
InventoryController.prototype._cooldownEnd = 0;
/**
 * The list of current InventoryPowerUpSlotModel
 * @type {Array}
 * @private
 */
InventoryController.prototype._powerUpSlots = [];
/**
 * If this controller was already initialized
 * @type {boolean}
 * @private
 */
InventoryController.prototype._isInitialized = false;
/**
 * Stores the difference (in milliseconds) between local and remote time.
 * This is used for controlling powerups cool-down
 * @type {number}
 * @private
 */
InventoryController.prototype._serverTimeDiff = 0;
/**
 * Variable to control the number of filled powerups.
 * It's used for improving performance on the update function (handleBeginStep)
 * @type {number}
 * @private
 */
InventoryController.prototype._filledPowerUps = 0;
InventoryController.prototype._powerUpDoneNotificationId = null;
/**
 * Initializes the inventory controller, doing the sync with the games backend
 * @param callback The callback to be triggered when the initialization finishes
 */
InventoryController.prototype.initialize = function (callback) {
    "use strict";
    this._syncServerTime(this, callback);
};
/**
 * Syncs the time with the backend server.
 * @param self The InventoryController object
 * @param callback A callback to be triggered after syncing is done
 * @private
 */
InventoryController.prototype._syncServerTime = function (self, callback) {
    "use strict";
    // syncing local and remote time (used for cool-down stuff)
    self.serverTime = Date.now();
    self._serverTimeDiff = 0;
    self._syncPowerUps(self, callback);
};
/**
 * This method will check the backend for any previous saved power up slots.
 * If it's not found, it will initialize the slots with default configuration.
 * @param self The InventoryController object
 * @param callback A callback to be triggered after syncing is done
 * @private
 */
InventoryController.prototype._syncPowerUps = function (self, callback) {
    "use strict";
    // checking remote server for powerups data
    YoutubeStorageManager.getPlayerData(["powerUps", "cooldownEnd"], function (data) {
        var powerUps = data["powerUps"];
        if (!powerUps) {
            // initialize powerups for the first time
            self._powerUpSlots = InventoryController._createDefaultPowerUps(self);
            // save powerups on server side
            YoutubeStorageManager.savePlayerData(["powerUps"], [InventoryController._modelToPersistentData(self._powerUpSlots, self._serverTimeDiff)], function () {
                self._isInitialized = true;
                // scheduling message on instant gamebot
                if (callback) {
                    callback();
                }
            }, function (error) {
                throw new Error("Error while saving powerup data on Server");
            });
        }
        else {
            // initialize powerups from remote data (and update them)
            self._powerUpSlots = InventoryController._persistentDataToModel(self, powerUps, self._serverTimeDiff);
            self._isInitialized = true;
            // scheduling message on instant gamebot
            if (callback) {
                callback();
            }
        }
        var cooldownEnd = data["cooldownEnd"];
        if (cooldownEnd) {
            self._cooldownEnd = cooldownEnd - self._serverTimeDiff;
        }
    });
};
/**
 * Utility method to create default power up slots.
 * It will assign the TNT to the first slot and set the refill time for all the others.
 * @param controller The InventoryController instance
 * @returns {Array} A list of InventoryPowerUpSlotModel
 * @private
 */
InventoryController._createDefaultPowerUps = function (controller) {
    "use strict";
    var powerUps = [];
    for (var i = 0; i < controller._powerUpSettings.maxSlotAmount; i++) {
        var slot = new InventoryPowerUpSlotModel();
        slot.unlocked = i < controller._powerUpSettings.startingSlotAmount;
        if (slot.unlocked) {
            slot.powerUp = i;
        }
        powerUps.push(slot);
    }
    controller._filledPowerUps = controller._powerUpSettings.startingSlotAmount;
    return powerUps;
};
/**
 * Utility method to convert existing models to persistent data which can be saved on the remote server
 * @param powerUps The list of InventoryPowerUpSlotModel
 * @param serverTimeDiff The time difference between local and server times
 * @returns {Array}
 * @private
 */
InventoryController._modelToPersistentData = function (powerUps, serverTimeDiff) {
    "use strict";
    var data = [];
    for (var i = 0; i < powerUps.length; i++) {
        var slot = powerUps[i];
        data.push({
            "unlocked": slot.unlocked,
            "powerUp": slot.powerUp
        });
    }
    return data;
};
/**
 * Utility method to convert persistent data from the server to a model object
 * @param controller The InventoryController instance
 * @param dataArray The persistent data stored on the server
 * @param serverTimeDiff The difference between local and remote server time
 * @param saveOnDataChanges If changes on slot data need to be pushed to the server (default = true)
 * @returns {Array}
 * @private
 */
InventoryController._persistentDataToModel = function (controller, dataArray, serverTimeDiff, saveOnDataChanges) {
    "use strict";
    if (saveOnDataChanges === undefined) {
        saveOnDataChanges = true;
    }
    var powerUpSlots = [];
    var wasDataChanged = false;
    controller._filledPowerUps = 0;
    for (var i = 0; i < dataArray.length; i++) {
        var data = dataArray[i];
        if (i >= controller._powerUpSettings.maxSlotAmount) {
            if (dataArray[i] !== null) {
                //preventing to save on GameSparks everytime even when nothing has changed
                dataArray[i] = null;
                wasDataChanged = true;
            }
            continue;
        }
        var slot = new InventoryPowerUpSlotModel();
        slot.unlocked = data["unlocked"];
        slot.powerUp = data["powerUp"];
        if (slot.powerUp >= 0) {
            controller._filledPowerUps = (i + 1);
        }
        powerUpSlots.push(slot);
    }
    if (saveOnDataChanges && wasDataChanged) {
        YoutubeStorageManager.savePlayerData(["powerUps"], [dataArray], function () {
        }, function (error) {
            console.log('Something went wrong while saving player data to the backend: ', error);
        });
    }
    return powerUpSlots;
};
/**
 * Utility method to randomize a power up for a slot
 * @param powerUpSlot The InventoryPowerUpSlotModel object
 * @private
 */
InventoryController.prototype._fillPowerUpSlot = function (powerUpSlot, powerUpIndex) {
    // powerUpSlot.remainingTime = 0;
    var availablePowerUps = this._powerUpSettings.options.length;
    powerUpSlot.powerUp = powerUpIndex !== undefined ? powerUpIndex : Math.floor(Math.random() * availablePowerUps);
};
/**
 * Gets the list of power up slots from the controller.
 * (Please, do not change values from View layer)
 * @returns {Array} The InventoryPowerUpSlotModel list
 */
InventoryController.prototype.getPowerUpSlots = function () {
    "use strict";
    if (!this._isInitialized) {
        throw new Error("InventoryController was not initialized");
    }
    // TODO -- clone the objects so they can be cached/changed by the View layer?
    return this._powerUpSlots;
};
/**
 * Gets the number of slots that are filled with a powerup.
 */
InventoryController.prototype.getFilledPowerUps = function () {
    "use strict";
    return this._filledPowerUps;
};
InventoryController.prototype.showRefillNotification = function () {
    "use strict";
    return (this._filledPowerUps <= MiscSettings.exclamationMarkPowerUpMin) ? true : false;
};
InventoryController.prototype.unlockAllPowerUps = function (powerUpCollection) {
    "use strict";
    for (var i = 0; i < this._powerUpSlots.length; i++) {
        var slot = this._powerUpSlots[i];
        var powerUpIndex = (powerUpCollection && powerUpCollection.constructor === Array && (powerUpCollection[i] !== undefined)) ? powerUpCollection[i] : undefined;
        if (slot.unlocked) {
            this._fillPowerUpSlot(slot, powerUpIndex);
        }
    }
    this._filledPowerUps = PowerUpSettings.maxSlotAmount;
    //console.log(this._powerUpSlots);
    YoutubeStorageManager.savePlayerData(["powerUps"], [InventoryController._modelToPersistentData(this._powerUpSlots, this._serverTimeDiff)], function () {
    }, function (error) {
        throw new Error("Error while saving powerup data on Server");
    });
};
/**
 * Consumes the power up and reorganize the list.
 * Every consumed power up is moved to the end of the list and it's setup to refill.
 * @param {number} powerUpIndex The index of the power up to be consumed
 * @returns {Array} The updated power up list (convenience)
 */
InventoryController.prototype.consumePowerUp = function (powerUpIndex) {
    "use strict";
    if (!this._isInitialized) {
        throw new Error("InventoryController was not initialized");
    }
    if (powerUpIndex < 0 || powerUpIndex > this._powerUpSlots.length) {
        console.error("Invalid power up index: ", powerUpIndex);
    }
    else if (this._powerUpSlots[powerUpIndex].powerUp < 0) {
        console.error("Cannot use selected power up index: ", powerUpIndex);
    }
    else {
        // grabbing a reference for the used power up slot
        var slot = this._powerUpSlots[powerUpIndex];
        var newSlotPosition = powerUpIndex;
        // shifting power ups (removing the used one)
        for (var i = powerUpIndex; i < this._powerUpSlots.length - 1; i++) {
            if (this._powerUpSlots[i + 1].unlocked) {
                this._powerUpSlots[i] = this._powerUpSlots[i + 1];
                newSlotPosition++;
            }
            else {
                break;
            }
        }
        // putting the used slot at the correct position
        this._powerUpSlots[newSlotPosition] = slot;
        // setting correct state for the used power up
        slot.powerUp = -1;
        this._filledPowerUps--;
        // saving it on the server side
        YoutubeStorageManager.savePlayerData(["powerUps"], [InventoryController._modelToPersistentData(this._powerUpSlots, this._serverTimeDiff)], function () {
        }, function (error) {
            throw new Error("Error while saving powerup data on Server");
        });
    }
    // TODO -- clone the objects so they can be cached/changed by the View layer?
    return this._powerUpSlots;
};
/**
 * Get the time left untill the next available inventory refresh
 * @returns {Number} the remaining cooldown
 */
InventoryController.prototype.getCooldown = function () {
    return this._cooldownEnd - Date.now();
};
/**
 * Get a formatted string containing the time left untill the next available inventory refresh
 * @returns {String} the remaining cooldown string
 */
InventoryController.prototype.getCooldownText = function () {
    return " ";
    var date = new Date(this.getCooldown());
    var h = date.getUTCHours();
    var m = date.getUTCMinutes();
    var s = date.getUTCSeconds();
    if (s < 10) {
        s = "0" + s;
    }
    return ((h * 60) + m) + ":" + s;
};
InventoryController.prototype.resetCooldown = function () {
    this._cooldownEnd = Date.now() + PowerUpSettings.adCooldown;
    YoutubeStorageManager.savePlayerData(["cooldownEnd"], [this._cooldownEnd + this._serverTimeDiff], function () {
        console.log("Saved cooldown end time to server");
    }, function (error) {
        throw new Error("Error while saving cooldown data on Server: " + error);
    });
};
